/**
 * PhotoChoiceScreen component - Choice screen for image generation source.
 *
 * Combines vision editing with image source selection in a single screen.
 * The participant can review and edit their AI-generated conversation summary
 * before choosing how to generate their vision image.
 *
 * When grounding images are available (Tier 1 or 2 cascade):
 *   Renders GroundingImagePicker, which includes its own "create from scratch" action.
 *
 * When no grounding images are available or loading fails:
 *   Renders a single "Generate Fresh" button (text-to-image from conversation themes).
 *
 * Design: DENiZEN — matches Figma node 1:494 ("Surface Pro 8 - 11")
 * - "Your Vision" heading centred (SN Pro Bold)
 * - Editable vision description textarea with character count
 * - "Create Your Vision" heading + creation options
 * - Camera/upload cards removed (PR #270) — only "Generate Fresh" + grounding images shown
 * - WCAG 2.1 AA compliant
 */

import { useState, useCallback } from 'react'
import { GroundingImagePicker } from './GroundingImagePicker'
import type { GroundingImageOption } from '@/services/api'

/** Maximum characters allowed in vision description */
const MAX_CHARACTERS = 2000
/** Minimum characters required before actions are enabled */
const MIN_CHARACTERS = 20

export interface PhotoChoiceScreenProps {
  /** Callback when user confirms their vision description and proceeds */
  onConfirm: (editedDescription: string) => void
  /** Whether image generation is in progress */
  isLoading?: boolean
  /** Conversation summary to display and allow editing */
  conversationSummary?: string
  /** Grounding images from tiered cascade (Tier 1 or 2) */
  groundingImages?: GroundingImageOption[]
  /** Callback when user selects a grounding image (receives imageId and edited vision) */
  onGroundingImageSelect?: (imageId: string, editedDescription: string) => void
  /** Whether grounding images are still loading */
  groundingImagesLoading?: boolean
  /** Location name for grounding image section header */
  groundingLocation?: string
  /** Error from grounding image fetch (non-blocking) */
  groundingError?: string | null
}

export function PhotoChoiceScreen({
  onConfirm,
  isLoading = false,
  conversationSummary,
  groundingImages,
  onGroundingImageSelect,
  groundingImagesLoading = false,
  groundingLocation,
  groundingError,
}: PhotoChoiceScreenProps) {
  const [description, setDescription] = useState(conversationSummary ?? '')
  const [originalDescription] = useState(conversationSummary ?? '')

  const isEdited = description !== originalDescription
  const characterCount = description.length
  const isValid = description.trim().length >= MIN_CHARACTERS
  const isNearLimit = characterCount > MAX_CHARACTERS * 0.9
  const showMinWarning = !isValid && description.trim().length > 0

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value
      if (value.length <= MAX_CHARACTERS) {
        setDescription(value)
      }
    },
    [],
  )

  const handleReset = useCallback(() => {
    setDescription(originalDescription)
  }, [originalDescription])

  const handleConfirm = useCallback(() => {
    if (!isValid) return
    onConfirm(description.trim())
  }, [description, isValid, onConfirm])

  const handleGroundingSelect = useCallback(
    (imageId: string) => {
      if (!isValid) return
      onGroundingImageSelect?.(imageId, description.trim())
    },
    [description, isValid, onGroundingImageSelect],
  )

  return (
    <div className="px-8 sm:px-12 animate-slide-up">

      {/* "Your Vision" heading */}
      <header className="mb-4 text-center">
        <h1 className="text-3xl font-display font-bold text-dark-green">
          Your Vision
        </h1>
      </header>

      {/* Editable vision description — always shown so users can type their own */}
      <div className="p-6 mb-4 border border-dark-green/10 rounded-2xl bg-white/40 max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-display font-bold text-dark-green flex items-center gap-2">
            <span className="w-3 h-3 inline-block rounded-full bg-mid-green border border-dark-green/15" />
            Vision Description
          </h2>
          {isEdited && originalDescription.length > 0 && (
            <button
              onClick={handleReset}
              className="text-xs font-body font-bold text-muted hover:text-dark-green transition-colors"
              type="button"
              aria-label="Reset to original description"
            >
              Reset to original
            </button>
          )}
        </div>

        {/* Location badge — non-editable, shows participant their area is factored in */}
        {groundingLocation && (
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 text-xs font-body font-bold text-dark-green/70 bg-dark-green/5 border border-dark-green/10 rounded-full px-3 py-1">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 0 1 15 0Z" />
              </svg>
              {groundingLocation}
            </span>
          </div>
        )}

        <textarea
          value={description}
          onChange={handleDescriptionChange}
          disabled={isLoading}
          rows={4}
          className="w-full p-4 font-body text-dark-green resize-y focus:outline-none focus-visible:ring-4 focus-visible:ring-mid-green focus-visible:ring-offset-2 transition-colors disabled:opacity-60 disabled:cursor-not-allowed border border-dark-green/15 rounded-2xl"
          style={{ fontSize: '0.875rem', lineHeight: '1.6', backgroundColor: isLoading ? '#f5f5f5' : '#ffffff' }}
          placeholder="Describe the vision for your community's digital future..."
          aria-label="Vision description for image generation"
          aria-describedby={`char-count${showMinWarning ? ' vision-help' : ''}`}
        />

        <div className="flex items-center justify-between mt-3">
          <div className="flex items-center gap-3">
            {isEdited && (
              <span className="text-xs font-body font-bold px-2 py-1 bg-dark-green/10 text-dark-green border border-dark-green/20 rounded-full">
                Edited
              </span>
            )}
            {showMinWarning && (
              <span className="text-xs font-body text-error" id="vision-help">
                Minimum {MIN_CHARACTERS} characters required
              </span>
            )}
          </div>
          <span
            id="char-count"
            className={`text-xs font-body font-bold ${isNearLimit ? 'text-error' : 'text-muted'}`}
            aria-live="polite"
          >
            {characterCount} / {MAX_CHARACTERS}
          </span>
        </div>
      </div>

      <hr className="border-dark-green/15 mb-5" />

      {/* "Create Your Vision" heading + subtitle */}
      <div className="text-center mb-5">
        <h2 className="text-2xl font-display font-bold text-dark-green mb-2">
          Create Your Vision
        </h2>
        <p className="text-base font-body text-dark-green/80">
          Choose how you would like to create your future vision
        </p>
      </div>

      {/* Grounding images loading skeleton */}
      {groundingImagesLoading && (
        <div className="p-6 border border-dark-green/10 rounded-2xl bg-white/40 animate-pulse mb-6">
          <div className="h-5 bg-stone border border-dark-green/10 rounded-2xl w-48 mb-3" />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="aspect-square bg-stone border border-dark-green/10 rounded-2xl" />
            ))}
          </div>
        </div>
      )}

      {/* Grounding images (Tier 1 or 2) */}
      {!groundingImagesLoading && groundingImages && groundingImages.length > 0 && onGroundingImageSelect && (
        <GroundingImagePicker
          images={groundingImages}
          onSelect={handleGroundingSelect}
          onCreateFromScratch={handleConfirm}
          isDisabled={isLoading || !isValid}
          location={groundingLocation}
        />
      )}

      {/* Non-blocking warning when grounding images failed to load */}
      {groundingError && (!groundingImages || groundingImages.length === 0) && (
        <p className="text-sm font-body text-dark-green border border-dark-green/20 bg-dark-green/5 px-3 py-2 rounded-2xl mb-6">
          We could not load location photos — you can still create your image from scratch
        </p>
      )}

      {/* Generate Fresh — shown when no grounding images */}
      {(!groundingImages || groundingImages.length === 0) && !groundingImagesLoading && (
        <div className="flex flex-col items-center gap-3">
          {/* Generate Fresh card — matches the Figma option card style */}
          <div className="border-2 border-dark-green rounded-[10px] p-6 text-center w-full max-w-[240px] bg-light-green/20">
            <div className="flex justify-center mb-3">
              <SparklesIcon />
            </div>
            <p className="font-display font-semibold text-xl text-dark-green mb-2">Generate Fresh</p>
            <p className="text-sm font-body text-dark-green/70 leading-relaxed">
              Create an entirely new image from your conversation
            </p>
          </div>

          <button
            type="button"
            onClick={handleConfirm}
            disabled={isLoading || !isValid}
            className="btn-denizen-primary w-full max-w-[300px] py-3"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner />
                <span>Generating...</span>
              </span>
            ) : (
              <span>Continue →</span>
            )}
          </button>
        </div>
      )}
    </div>
  )
}

function SparklesIcon() {
  return (
    <svg
      className="w-12 h-12 text-mid-green"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function LoadingSpinner() {
  return (
    <svg
      className="w-5 h-5 animate-spin"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="m4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 0 1 4 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  )
}

export default PhotoChoiceScreen
